/**
 * @module breakpoints
 * Centralized theme breakpoint definitions.
 *
 * Configured in app.js via THEME_CONFIG.breakpoints and enforces max-width semantics:
 * - 'xs': window.innerWidth <= breakpoints.xs (e.g. 640px)
 * - 'md': window.innerWidth <= breakpoints.md (e.g. 1080px)
 * - 'lg': window.innerWidth > breakpoints.md  (desktop up to lg max-width)
 */

let siteBreakpoints = {
  xs: 640,
  md: 1080,
  lg: 1440,
};

function getCssBreakpointVars() {
  if (typeof window === 'undefined' || !document.documentElement) return {};
  const s = getComputedStyle(document.documentElement);
  const xs = parseInt(s.getPropertyValue('--breakpoint-xs'), 10);
  const md = parseInt(s.getPropertyValue('--breakpoint-md'), 10);
  const lg = parseInt(s.getPropertyValue('--breakpoint-lg'), 10);
  return {
    xs: !isNaN(xs) && xs > 0 ? xs : undefined,
    md: !isNaN(md) && md > 0 ? md : undefined,
    lg: !isNaN(lg) && lg > 0 ? lg : undefined,
  };
}

/**
 * Configure site-wide breakpoint values (called from app.js).
 * @param {{ xs?: number, md?: number, lg?: number }} config
 */
export function setBreakpoints(config = {}) {
  const cssVars = getCssBreakpointVars();
  siteBreakpoints = {
    xs: config.xs ?? cssVars.xs ?? 640,
    md: config.md ?? cssVars.md ?? 1080,
    lg: config.lg ?? cssVars.lg ?? 1440,
  };
}

/**
 * Returns current site breakpoint pixel values.
 * @returns {{ xs: number, md: number, lg: number }}
 */
export function getBreakpoints() {
  return siteBreakpoints;
}

/**
 * Resets/re-applies breakpoint configuration.
 */
export function resetBreakpointCache() {
  const cssVars = getCssBreakpointVars();
  siteBreakpoints = {
    xs: siteBreakpoints.xs ?? cssVars.xs ?? 640,
    md: siteBreakpoints.md ?? cssVars.md ?? 1080,
    lg: siteBreakpoints.lg ?? cssVars.lg ?? 1440,
  };
}

/**
 * Evaluates current window.innerWidth against max-width breakpoint boundaries.
 * @returns {'xs'|'md'|'lg'} Active breakpoint identifier
 */
export function getCurrentBreakpoint() {
  const { xs, md } = getBreakpoints();
  const width = window.innerWidth;
  if (width <= xs) return 'xs';
  if (width <= md) return 'md';
  return 'lg';
}

/**
 * Checks if current window width is at or below a given max-width breakpoint ('xs', 'md', 'lg').
 * @param {'xs'|'md'|'lg'} bp
 * @returns {boolean}
 */
export function isMaxBreakpoint(bp) {
  const bps = getBreakpoints();
  return window.innerWidth <= (bps[bp] ?? Infinity);
}
